import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PACKS } from "../components/Form";
import { sendGetRequest, sendPostRequest } from "../utils";

const GENERIC_ERROR = "Une erreur est survenue. Veuillez réessayer plus tard.";

const Resubscribe = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();

  // loading | ready | active | done | error
  const [view, setView] = useState("loading");
  const [firstName, setFirstName] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    const load = async () => {
      try {
        // Coming back from Stripe: confirm the payment. Otherwise: greet.
        const res = sessionId
          ? await sendPostRequest(`resubscribe/${token}/confirm`, {
              session_id: sessionId,
            })
          : await sendGetRequest(`resubscribe/${token}`);
        const data = await res.json();
        if (!isCurrent) return;

        if (data.errMessage) {
          setError(data.errMessage);
          setView(data.code === "active" ? "active" : "error");
        } else if (sessionId) {
          setView("done");
        } else {
          setFirstName(data.firstName);
          setView(data.state === "active" ? "active" : "ready");
        }
      } catch (err) {
        if (!isCurrent) return;
        setError(GENERIC_ERROR);
        setView("error");
      }
    };

    load();
    return () => {
      isCurrent = false;
    };
  }, [token, sessionId]);

  // Pressing Back on Stripe's page can bring this page back exactly as it
  // was left, button still disabled; make it usable again
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setIsSubmitting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const doCheckout = async () => {
    setIsSubmitting(true);
    try {
      const res = await sendPostRequest(`resubscribe/${token}/checkout`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError(data.errMessage || GENERIC_ERROR);
      setView(data.code === "active" ? "active" : "error");
    } catch (err) {
      setError(GENERIC_ERROR);
      setView("error");
    }
    setIsSubmitting(false);
  };

  const homeButton = (
    <button
      className="bg-yellow-600 text-white py-2 px-4 rounded-md block mx-auto hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
      onClick={() => navigate("/")}
    >
      Retour à l'accueil
    </button>
  );

  return (
    <div className="bg-slate-200 min-h-screen flex justify-center items-center p-4">
      <div className="bg-white p-10 rounded-md shadow-md max-w-xl w-full">
        {view === "loading" && (
          <p className="text-lg text-center">Un instant...</p>
        )}

        {view === "ready" && (
          <>
            <h1 className="text-3xl font-semibold mb-4 text-center">
              Bon retour, {firstName}!
            </h1>
            <p className="text-lg mb-2 text-center">
              Réabonnez-vous au Club Sutton Encore et continuez de recevoir vos
              avantages VIP par texto.
            </p>
            <p className="text-lg mb-6 text-center font-semibold">
              {PACKS[0].label}
            </p>
            <button
              className="bg-yellow-600 text-white py-2 px-4 rounded-md block mx-auto hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 disabled:opacity-60"
              onClick={doCheckout}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Un instant..." : "Me réabonner"}
            </button>
          </>
        )}

        {view === "active" && (
          <>
            <h1 className="text-3xl font-semibold mb-4 text-center">
              Vous êtes déjà membre
            </h1>
            <p className="text-lg mb-6 text-center">
              Votre abonnement au Club Sutton Encore est actif. Aucune action
              n'est requise.
            </p>
            {homeButton}
          </>
        )}

        {view === "done" && (
          <>
            <h1 className="text-3xl font-semibold mb-4 text-center">
              Vous êtes réabonné avec succès!
            </h1>
            <p className="text-lg mb-6 text-center">
              Félicitations! Vous êtes de nouveau membre du Club Sutton Encore!
              Vanessa communiquera avec vous par texto pour les avantages VIP
            </p>
            {homeButton}
          </>
        )}

        {view === "error" && (
          <>
            <h1 className="text-3xl font-semibold mb-4 text-center">Oups!</h1>
            <p className="text-lg mb-6 text-center">{error}</p>
            {homeButton}
          </>
        )}
      </div>
    </div>
  );
};

export default Resubscribe;
